import {makeObservable, observable, runInAction} from "mobx";
import {GameInfo} from "../games/game-info";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {ServiceBase} from "../ServiceBase";
import {ISlideRenderer, IVideoSlideDataStorage, IVideoSlidesService} from "./video-slides.service.interface";
import {VideoSlideRenderer} from "./video-slides-renderers/video-slide.renderer";
import {LiveStreamSlideRenderer} from "./video-slides-renderers/live-stream-slide.renderer";
import {PromotionSlideRenderer} from "./video-slides-renderers/promotion-slide.renderer";
import {ApiListType, ArenaConfiguration} from "../configurations/config-data-sections/arenas.configuration";
import {VIDEO_SLIDES_ARENA} from "../arenas/arena-consts";
import {LocalStorageKeys} from "../storage/local-storage-keys";

export class VideoSlidesService extends ServiceBase implements IVideoSlidesService {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.reaction(() => this.services.games.gamesMap,
            () => {
                this._updateVideoSlideGames();
                this._updateVideoSlideList();
            });

        makeObservable(this);
    }

    @observable
    private _slideList: ISlideRenderer[] = [];
    get slideList(): ISlideRenderer[] {
        return this._slideList;
    }

    get slideIndex(): number {
        const videoSlideDataFromLocalStorage = this.services.localStorage.getJson<IVideoSlideDataStorage>(LocalStorageKeys.videoSlides);

        if (!videoSlideDataFromLocalStorage?.lastVideoSlideIndex) {
            return 0;
        }

        const gameTypesBasedOnLocalStorageIndex = this._getGameTypesFromSlideList(videoSlideDataFromLocalStorage.lastVideoSlideIndex);
        if (this.services.tools.getHash(gameTypesBasedOnLocalStorageIndex) !== videoSlideDataFromLocalStorage.videoSlideGamesHash) {
            return 0;
        }

        return videoSlideDataFromLocalStorage.lastVideoSlideIndex;
    }

    set slideIndex(value: number) {
        setTimeout(() => {
            const gameTypesVideosDisplayedByNow = this._getGameTypesFromSlideList(value);
            const videoSlideDataStorage: IVideoSlideDataStorage = {
                videoSlideGamesHash: this.services.tools.getHash(gameTypesVideosDisplayedByNow),
                lastVideoSlideIndex: value,
            };
            this.services.localStorage.setJson(LocalStorageKeys.videoSlides, videoSlideDataStorage);
        });
    }

    private _getGameTypesFromSlideList(endIndex: number): number[] {
        return this._slideList.slice(0, endIndex).filter(sl => sl.gameInfo).map(sl => sl.gameInfo.gameType);
    }

    private _staticGamesInArena: GameInfo[] = [];
    private _liveStreamGamesInArena: GameInfo[] = [];
    private _staticFromCdnGamesInArena: GameInfo[] = [];    

    private get config(): ArenaConfiguration {
        return this.services.configuration.data.arenas[VIDEO_SLIDES_ARENA];
    }

    private _updateVideoSlideGames(): void {
        this.config.swiperGamesList?.forEach(swiperList => {
            switch (swiperList.apiListType) {
                case ApiListType.STATIC_VIDEO:
                    this._staticFromCdnGamesInArena = this._getStaticSlideVideoGames([...swiperList.gamesId]);
                    break;
                case ApiListType.LIVE_CASINO_VIDEO:
                    this._liveStreamGamesInArena = this._getLiveStreamVideoGames([...swiperList.gamesId]);
                    break;
                default:
                    //this will be changed to a dedicated new API List type
                    this._staticGamesInArena = this._getStaticSlideVideoGames([...swiperList.gamesId]);
                    break;
            }
        });
    }

    private _getMaxNoOfChunks(): number {
        let maxNoOfChunksInAllCategories = 0;

        this.config.slidesRules.forEach((rule) => {
            switch (rule.category) {
                case "staticVideo":
                    let maxNoOfChunksInStaticVideo = Math.ceil(this._staticGamesInArena.length / rule.slidesChunkSize);
                    if (maxNoOfChunksInStaticVideo > maxNoOfChunksInAllCategories) {
                        maxNoOfChunksInAllCategories = maxNoOfChunksInStaticVideo;
                    }
                    break;
                case "liveStreamVideo":
                    let maxNoOfChunksInLiveStreamVideo = Math.ceil(this._liveStreamGamesInArena.length / rule.slidesChunkSize);
                    if (maxNoOfChunksInLiveStreamVideo > maxNoOfChunksInAllCategories) {
                        maxNoOfChunksInAllCategories = maxNoOfChunksInLiveStreamVideo;
                    }
                    break;
            }
        });

        return maxNoOfChunksInAllCategories;
    }

    private _updateVideoSlideList(): void {
        let currentSlideList: ISlideRenderer[] = [];

        //this implementation is just for POC purposes
        this._staticFromCdnGamesInArena.forEach(staticVideo =>
            currentSlideList.push(new VideoSlideRenderer(this.services.tools.getGuid(), staticVideo, true))
        );

        const maxNoOfChunksInAllCategories = this._getMaxNoOfChunks();
        for (let chunk_index = 0; chunk_index < maxNoOfChunksInAllCategories; chunk_index++) {
            this.config.slidesRules.forEach(rule => {
                switch (rule.category) {
                    case "staticVideo":
                        let staticVideosBatch = this._staticGamesInArena.splice(0, rule.slidesChunkSize);
                        staticVideosBatch.forEach(staticVideo =>
                            currentSlideList.push(new VideoSlideRenderer(this.services.tools.getGuid(), staticVideo))
                        );
                        break;
                    case "liveStreamVideo":
                        let liveVideosBatch = this._liveStreamGamesInArena.splice(0, rule.slidesChunkSize);
                        liveVideosBatch.forEach(liveStreamVideo =>
                            currentSlideList.push(new LiveStreamSlideRenderer(this.services.tools.getGuid(), liveStreamVideo))
                        );
                        break;
                    case "banner":
                        //we don't care about the rule.slidesCount for banner as we show it only once
                        currentSlideList.push(new PromotionSlideRenderer(this.services.tools.getGuid()));
                        break;
                }
            });
        }

        runInAction(() => {
            // we triple the size of the slideList, by replicating the currentList, in order to fake swipe looping
            this._slideList = currentSlideList.concat(currentSlideList).concat(currentSlideList);
        });
    }

    private _getStaticSlideVideoGames(gameTypes: number[]): GameInfo[] {
        let gameInfos: GameInfo[] = [];

        gameTypes.forEach(gameType => {
            if (this.services.games.gamesMap[gameType]?.slideVideo) { 
                gameInfos.push(this.services.games.gamesMap[gameType]);
            }
        });

        return gameInfos;
    }

    private _getLiveStreamVideoGames(gameTypes: number[]): GameInfo[] {
        let gameInfos: GameInfo[] = [];

        gameTypes.forEach(gameType => {
            if (this.services.games.gamesMap[gameType]?.videoURL) { 
                gameInfos.push(this.services.games.gamesMap[gameType]);
            }
        });

        return gameInfos;
    }
}
